import { BaseApiService } from './BaseApiService';

export interface SchedulingRun {
  id: number;
  run_type: 'auto' | 'manual' | 'bulk';
  status: 'success' | 'partial' | 'failed';
  total_orders: number;
  scheduled_orders: number;
  failed_orders: number;
  run_duration_ms: number;
  created_at: string;
  created_by?: number;
}

export interface SchedulingError {
  id: number;
  run_id: number;
  order_id?: number;
  product_id?: number;
  error_type: string;
  error_message: string;
  created_at: string;
}

export interface SchedulingRunWithErrors extends SchedulingRun {
  errors: SchedulingError[];
}

export interface ErrorStatistics {
  error_type: string;
  count: number;
}

class SchedulingRunsService extends BaseApiService {
  // Get the last N scheduling runs
  async getLastSchedulingRuns(limit: number = 5): Promise<SchedulingRun[]> {
    const response = await this.get<SchedulingRun[]>(`/scheduling-runs/last/${limit}`);
    return response || [];
  }

  // Get scheduling runs with pagination
  async getSchedulingRuns(
    page: number = 1,
    pageSize: number = 20,
    status?: string
  ): Promise<{ runs: SchedulingRun[]; total: number }> {
    const params: Record<string, any> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
    };

    if (status) {
      params.status = status;
    }

    const response = await this.get<{ runs: SchedulingRun[]; total: number }>(
      '/scheduling-runs',
      params
    );
    return response || { runs: [], total: 0 };
  }

  // Get a specific scheduling run with errors
  async getSchedulingRunWithErrors(runId: number): Promise<SchedulingRunWithErrors> {
    const response = await this.get<SchedulingRunWithErrors>(`/scheduling-runs/${runId}`);
    if (!response) {
      throw new Error('Failed to get scheduling run');
    }
    return response;
  }

  // Get errors for a specific run
  async getSchedulingRunErrors(runId: number): Promise<SchedulingError[]> {
    const response = await this.get<SchedulingError[]>(`/scheduling-runs/${runId}/errors`);
    return response || [];
  }

  // Get error statistics
  async getErrorStatistics(days: number = 30): Promise<ErrorStatistics[]> {
    const response = await this.get<ErrorStatistics[]>('/scheduling-runs/stats/errors', {
      days: days.toString(),
    });
    return response || [];
  }

  // Format run duration for display
  formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(ms / 60000);
      const seconds = Math.floor((ms % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  }

  // Format run status for display
  formatStatus(status: string): { text: string; className: string } {
    switch (status) {
      case 'success':
        return { text: 'Success', className: 'status-success' };
      case 'partial':
        return { text: 'Partial', className: 'status-warning' };
      case 'failed':
        return { text: 'Failed', className: 'status-error' };
      default:
        return { text: status, className: 'status-unknown' };
    }
  }

  // Format run type for display
  formatRunType(runType: string): string {
    switch (runType) {
      case 'auto':
        return 'Auto';
      case 'manual':
        return 'Manual';
      case 'bulk':
        return 'Bulk';
      default:
        return runType;
    }
  }
}

export const schedulingRunsService = new SchedulingRunsService();
